import { motion } from "framer-motion";
import { Heart, Award, Users } from "lucide-react";

const PARAGRAPHS = [
  "Karlynn Cammon is the Founder and Chief Executive Officer of Play 2 Fitness Inc., a 501(c)(3) nonprofit organization dedicated to empowering youth and communities through fitness, wellness, and educational initiatives. She also serves as an accomplished Healthcare Executive currently holding the position of Associate Director of Operations at Leidos — bringing extensive expertise in organizational leadership, program development, and strategic strategies to both the public health and nonprofit sectors.",
  "Her lifelong dedication to service began at the age of nine, when she joined her parents and grandparents in feeding the homeless and supporting underserved populations. These early formative experiences instilled in her a deep commitment to social responsibility and motivated her ongoing efforts to address health disparities and expand access to essential resources. Over the years, she has continued this legacy through youth-focused wellness programs and strategic advocacy that create lasting community impact.",
  "Ms. Cammon's educational credentials and professional experience enhance her leadership capabilities — grounding her in healthcare systems, organizational management, and public health policy. She also carries key professional certifications that bolster her expertise in operational excellence.",
  "Through Play 2 Fitness Inc., Ms. Cammon blends her healthcare background and philanthropic dedication to develop innovative after-school fitness and wellness programs that foster physical activity, mental resilience, and holistic well-being. Under her leadership, the organization has become a powerful catalyst for positive change — providing inclusive opportunities that allow youth and families to thrive.",
  "Ms. Cammon exemplifies both professional excellence and a lifelong devotion to service, positioning her as a trusted advocate for health equity, community empowerment, and systemic change at the local, state, and federal levels.",
];

const PILLARS = [
  {
    icon: Heart,
    label: "Service since age 9",
    desc: "A lifelong commitment to underserved communities.",
  },
  {
    icon: Award,
    label: "Healthcare Executive",
    desc: "Associate Director of Operations at Leidos.",
  },
  {
    icon: Users,
    label: "Founder & CEO",
    desc: "Leading Play 2 Fitness Inc. since inception.",
  },
];

export default function AboutSection() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-20 md:py-32 bg-brand-sand relative overflow-hidden"
    >
      {/* Subtle accent shapes */}
      <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-orange-100 to-transparent blur-3xl opacity-60 pointer-events-none" />
      <div className="absolute bottom-0 -left-40 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-blue-50 to-transparent blur-3xl opacity-60 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Portrait column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="sticky top-28">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/hero/founder-karlynn.jpg"
                  alt="Karlynn Cammon, Founder & CEO of Play 2 Fitness"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/65 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <div className="text-xs font-bold uppercase tracking-widest text-brand-yellow">
                    Founder &amp; CEO
                  </div>
                  <div className="font-display font-black text-2xl md:text-3xl mt-1 leading-tight">
                    Karlynn Cammon
                  </div>
                </div>
              </div>

              {/* Three-up creds card */}
              <div className="mt-5 grid grid-cols-1 gap-2">
                {PILLARS.map((p) => {
                  const Icon = p.icon;
                  return (
                    <div
                      key={p.label}
                      className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-slate-200"
                    >
                      <div className="w-10 h-10 rounded-xl bg-brand-coral/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-brand-coral" />
                      </div>
                      <div>
                        <div className="font-bold text-brand-navy text-sm">
                          {p.label}
                        </div>
                        <div className="text-xs text-slate-600 leading-snug mt-0.5">
                          {p.desc}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Story column */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-coral mb-4">
                About · Our story
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-brand-navy">
                Built on a lifetime of{" "}
                <span className="text-brand-coral">service.</span>
              </h2>

              <div className="mt-8 space-y-5 text-base md:text-lg text-slate-700 leading-relaxed">
                {PARAGRAPHS.map((p, i) => (
                  <motion.p
                    key={i}
                    data-testid={`about-paragraph-${i}`}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                  >
                    {p}
                  </motion.p>
                ))}
              </div>

              {/* Pull-quote / closing accent */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: 0.5 }}
                className="mt-10 relative bg-brand-navy text-white rounded-3xl p-7 md:p-9 overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-brand-coral/20 rounded-full -mr-12 -mt-12 blur-2xl" />
                <div className="text-xs font-bold uppercase tracking-widest text-brand-yellow mb-3">
                  Why this work matters
                </div>
                <p className="font-display font-bold text-xl md:text-2xl leading-snug">
                  &ldquo;Every child — regardless of ability, background, or
                  zip code — deserves a place where they can move, learn,
                  connect, and thrive.&rdquo;
                </p>
                <div className="mt-4 text-sm text-slate-300">
                  Karlynn Cammon · Founder &amp; CEO
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
